import { CheckCircle2 } from "lucide-react";

/**
 * Pricing plan card used on the Pricing page and the Home page teaser.
 *
 * Props:
 *   name      - Plan display name (e.g. "Individual")
 *   price     - Price string (e.g. "$49")
 *   period    - Period string (e.g. "/yr")
 *   icon      - Lucide icon component
 *   highlight - When true renders the dark featured variant with "Most popular" pill
 *   features  - string[]
 *   cta       - React node rendered in the footer (current-badge, button, or Link)
 */
export function PricingCard({ name, price, period, icon: Icon, highlight, features, cta }) {
  return (
    <div
      className={`bg-card border border-border rounded-2xl px-7 py-8 relative flex flex-col gap-6${
        highlight ? " bg-[hsl(152,30%,14%)] border-[hsl(152,40%,30%)]" : ""
      }`}
    >
      {highlight && (
        <div className="absolute top-[-0.65rem] left-1/2 -translate-x-1/2 text-[0.6875rem] font-bold tracking-[0.07em] uppercase px-[0.8rem] py-[0.22rem] bg-brand-600 text-white rounded-full whitespace-nowrap">
          Most popular
        </div>
      )}

      <div className="flex flex-col gap-2">
        <div
          className={`w-9 h-9 rounded-[9px] flex items-center justify-center mb-1${
            highlight
              ? " bg-brand-400/20 text-brand-300"
              : " bg-brand-700/10 text-brand-600"
          }`}
        >
          <Icon size={18} />
        </div>
        <div
          className={`text-[0.8125rem] font-bold tracking-[0.07em] uppercase${
            highlight ? " text-brand-300" : " text-muted-foreground"
          }`}
        >
          {name}
        </div>
        <div
          className={`font-display text-[2.75rem] leading-none${
            highlight ? " text-white" : " text-foreground"
          }`}
        >
          {price}
          <span
            className={`font-sans text-[0.9rem] font-normal ml-[2px]${
              highlight ? " text-white/45" : " text-muted-foreground"
            }`}
          >
            {period}
          </span>
        </div>
      </div>

      <ul className="list-none p-0 m-0 flex flex-col gap-[0.65rem] flex-1">
        {features.map((f) => (
          <li
            key={f}
            className={`flex items-center gap-[0.6rem] text-[0.9rem]${
              highlight ? " text-white/70" : " text-muted-foreground"
            }`}
          >
            <CheckCircle2
              size={14}
              className={`shrink-0${highlight ? " text-brand-300" : " text-brand-600"}`}
            />
            {f}
          </li>
        ))}
      </ul>

      <div className="pt-2">{cta}</div>
    </div>
  );
}
